/**
 * DMP Section List
 *
 * Navigable list of DMP sections organized by group
 * with status indicators and placeholder counts.
 */

import { ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Edit3, RefreshCw, Loader2 } from 'lucide-react';
import { useState } from 'react';

interface SectionMeta {
  id: string;
  number: number;
  title: string;
  group: string;
  sourceType: string;
  status: string;
  placeholderCount: number;
}

interface DMPSectionListProps {
  sections: SectionMeta[];
  selectedSectionId: string | null;
  onSelectSection: (sectionId: string) => void;
  onRegenerateSection?: (sectionId: string) => void;
  regeneratingSectionId?: string | null;
}

const GROUP_LABELS: Record<string, string> = {
  context: 'Study Context & Management',
  processing: 'Data Definition & Processing',
  systems: 'Data Systems & Compliance',
};

const GROUP_ORDER = ['context', 'processing', 'systems'];

function StatusIcon({ status, placeholderCount }: { status: string; placeholderCount: number }) {
  if (status === 'reviewed') {
    return <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />;
  }
  if (status === 'edited') {
    return <Edit3 className="h-4 w-4 text-blue-500 flex-shrink-0" />;
  }
  if (placeholderCount > 0) {
    return <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
  }
  return <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />;
}

export default function DMPSectionList({
  sections,
  selectedSectionId,
  onSelectSection,
  onRegenerateSection,
  regeneratingSectionId,
}: DMPSectionListProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(GROUP_ORDER) // All expanded by default
  );

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const sectionsByGroup = GROUP_ORDER.map((group) => ({
    group,
    label: GROUP_LABELS[group],
    sections: sections.filter((s) => s.group === group),
  }));

  return (
    <div className="space-y-1">
      {sectionsByGroup.map(({ group, label, sections: groupSections }) => (
        <div key={group}>
          {/* Group header */}
          <button
            onClick={() => toggleGroup(group)}
            className="flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded"
          >
            {expandedGroups.has(group) ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {label}
            <span className="ml-auto text-gray-400 font-normal normal-case">
              {groupSections.filter((s) => s.status === 'reviewed' || (s.status === 'generated' && s.placeholderCount === 0)).length}/{groupSections.length}
            </span>
          </button>

          {/* Section items */}
          {expandedGroups.has(group) && (
            <div className="ml-2 space-y-0.5">
              {groupSections.map((section) => {
                const isRegenerating = regeneratingSectionId === section.id;
                return (
                  <div
                    key={section.id}
                    className={`group flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded transition-colors ${
                      selectedSectionId === section.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => onSelectSection(section.id)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      {isRegenerating ? (
                        <Loader2 className="h-4 w-4 text-blue-500 flex-shrink-0 animate-spin" />
                      ) : (
                        <StatusIcon
                          status={section.status}
                          placeholderCount={section.placeholderCount}
                        />
                      )}
                      <span className="text-gray-400 text-xs w-5 text-right flex-shrink-0">
                        {section.number}.
                      </span>
                      <span className="truncate flex-1">{section.title}</span>
                    </button>
                    {section.placeholderCount > 0 && !isRegenerating && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0">
                        {section.placeholderCount}
                      </span>
                    )}
                    {onRegenerateSection && !isRegenerating && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRegenerateSection(section.id);
                        }}
                        title="Regenerate this section"
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity flex-shrink-0"
                      >
                        <RefreshCw className="h-3 w-3 text-gray-400" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
